import i18n from '../../../../i18n';
import { msg, tr } from '../../../../i18n/msg';

/**
 * Durak card helpers. Wire code = rank label + suit letter: "6S", "10H", "QD", "AC".
 * Suits: S ♠ spades, H ♥ hearts, D ♦ diamonds, C ♣ clubs.
 */

export const SUIT_SYMBOL = { S: '♠', H: '♥', D: '♦', C: '♣' };
export const SUIT_NAME = {
  S: msg('card.suit.spades', 'spades'),
  H: msg('card.suit.hearts', 'hearts'),
  D: msg('card.suit.diamonds', 'diamonds'),
  C: msg('card.suit.clubs', 'clubs'),
};
const RANK_NAME = {
  6: '6',
  7: '7',
  8: '8',
  9: '9',
  10: '10',
  J: msg('card.rank.jack', 'Jack'),
  Q: msg('card.rank.queen', 'Queen'),
  K: msg('card.rank.king', 'King'),
  A: msg('card.rank.ace', 'Ace'),
};
const RANK_VALUE = {
  6: 6, 7: 7, 8: 8, 9: 9, 10: 10, J: 11, Q: 12, K: 13, A: 14,
};

/** "10H" → { code, rank:'10', value:10, suit:'H', red:true } (null for garbage). */
export function parseCard(code) {
  if (typeof code !== 'string' || code.length < 2) return null;
  const suit = code.slice(-1).toUpperCase();
  const rank = code.slice(0, -1).toUpperCase();
  if (!SUIT_SYMBOL[suit] || !RANK_VALUE[rank]) return null;
  return {
    code: `${rank}${suit}`, rank, value: RANK_VALUE[rank], suit, red: suit === 'H' || suit === 'D',
  };
}

export function cardLabel(code) {
  const c = parseCard(code);
  const t = i18n.t.bind(i18n);
  return c
    ? t('card.label', '{{rank}} of {{suit}}', { rank: tr(t, RANK_NAME[c.rank]), suit: tr(t, SUIT_NAME[c.suit]) })
    : t('card.generic', 'card');
}

export function isCourt(rank) {
  return rank === 'J' || rank === 'Q' || rank === 'K';
}

export function formatGram(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return String(Math.round(n * 10000) / 10000);
}

/** Prize of a stake: 2·stake·(1 − commission%). */
export function stakePrize(stake, commissionPercent) {
  const s = Number(stake) || 0;
  const c = Number(commissionPercent) || 0;
  return 2 * s * (1 - c / 100);
}

export function formatClock(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Horizontal layout of a fanned hand: per-card x offset, rotation and lift so that any number of cards
 * fits into `width` px. Returns [{x, rot, y}] centred around 0.
 */
export function fanLayout(n, { width = 340, cardW = 64, maxStep = 46, maxAngle = 4 } = {}) {
  if (n <= 0) return [];
  const step = n === 1 ? 0 : Math.min(maxStep, (width - cardW) / (n - 1));
  const angle = n === 1 ? 0 : Math.min(maxAngle, 36 / n);
  const mid = (n - 1) / 2;
  return Array.from({ length: n }, (_, i) => {
    const d = i - mid;
    return { x: d * step, rot: d * angle, y: Math.abs(d) * Math.abs(d) * 0.9 * (angle / 4) };
  });
}

export const REASON_TEXT = {
  WIN: msg('durak.reason.win', 'Out of cards'),
  DRAW: msg('durak.reason.draw', 'Both out of cards at once'),
  FORFEIT: msg('durak.reason.forfeit', 'Surrender'),
  TIMEOUT: msg('durak.reason.timeout', 'Too many timeouts'),
  DISCONNECT: msg('durak.reason.disconnect', 'Disconnected'),
};
